import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import type {
  AddCorrectionPayload,
  CorrectionAction,
  CorrectionField,
  CorrectionPreview,
  Discrepancy,
  DiscrepancyFile,
  PeopleSelection,
  PickedPeople,
} from "@models/Discrepancy.model";
import { formatDay } from "./DataIssuesPage";

interface Props {
  discrepancy: Discrepancy;
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}

type Who = "ids" | "missing" | "new";
type What = "stop_serving" | "keep_out" | "let_back" | "set_value";

const WHAT: Record<What, { action: CorrectionAction; allowReturn: boolean; title: string; detail: string }> = {
  stop_serving: {
    action: "stop_serving", allowReturn: false, title: "Stop serving",
    detail: "They stay in the population with their history, but no more ads. For people who left.",
  },
  keep_out: {
    action: "exclude", allowReturn: false, title: "Remove · keep them out",
    detail: "No more ads, and if a later file still carries them, their rows are ignored. For duplicates and records that should never have been sent.",
  },
  let_back: {
    action: "exclude", allowReturn: true, title: "Remove · let them back in",
    detail: "No more ads until a later file carries them again; that file brings them back with their history.",
  },
  set_value: {
    action: "set_value", allowReturn: false, title: "Fix a value",
    detail: "The client's file has it wrong. Load the right value instead, from every file, until you end the correction. Applies at this client, in every population these people are in.",
  },
};

/*
    New correction (MPN-2009, MPN-2122, MPN-2123): who → what happens to them → starting
    from → preview → apply. Starting from "the file it went wrong in" rebuilds from that file:
    what it and the later files produced is discarded and replayed with the correction. The
    preview says what that discards (conversions and their worth on the next invoice,
    estimated from the traces as they are) and whether the billed-month guard will refuse it.
    Apply is only offered for the inputs that were previewed.
*/
export default function AddCorrectionDialog({ discrepancy, open, onClose, onApplied }: Props) {
  const canCompare = Boolean(discrepancy.file);
  const [who, setWho] = useState<Who>(canCompare && discrepancy.kind === "missing_from_file" ? "missing" : "ids");
  const [ids, setIds] = useState("");
  const [picked, setPicked] = useState<PickedPeople | null>(null);
  const [what, setWhat] = useState<What>("stop_serving");
  const [fields, setFields] = useState<CorrectionField[]>([]);
  const [fieldKey, setFieldKey] = useState("");
  const [dimension, setDimension] = useState<Record<string, string>>({});
  const [value, setValue] = useState("");
  const [files, setFiles] = useState<DiscrepancyFile[]>([]);
  const [startRunId, setStartRunId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The preview, keyed by the inputs it was made for: a change to any of them discards it.
  const [preview, setPreview] = useState<{ key: string; result: CorrectionPreview } | null>(null);

  useEffect(() => {
    const service = new DiscrepancyService();
    service.files(discrepancy.id).then(setFiles).catch(() => setFiles([]));
    service.fields(discrepancy.id).then(setFields).catch(() => setFields([]));
  }, [discrepancy.id]);

  const field = fields.find((f) => `${f.entity_type}.${f.attr_key}` === fieldKey);
  // A held file has not loaded, so there is nothing of it to rebuild (MPN-2121).
  const pastStartAllowed = what !== "stop_serving" && !discrepancy.holds_file;
  const startFrom = pastStartAllowed ? startRunId : null;

  const selection = (): PeopleSelection => who === "ids"
    ? { ids: ids.split(/[\s,]+/).filter(Boolean) }
    : { compare_run_id: discrepancy.file?.id ?? null, side: who };

  const run = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const check = () => run(async () => {
    setPicked(await new DiscrepancyService().pickPeople(discrepancy.id, selection()));
  });

  const payload = (): AddCorrectionPayload => {
    const { action, allowReturn } = WHAT[what];
    return {
      ...selection(),
      action,
      allow_return: allowReturn,
      start_run_id: startFrom,
      ...(action === "set_value" && field
        ? { entity_type: field.entity_type, attr_key: field.attr_key, dimension, value }
        : {}),
    };
  };
  const payloadKey = JSON.stringify(payload());
  const previewed = preview?.key === payloadKey ? preview.result : null;

  const showPreview = () => run(async () => {
    setPreview({ key: payloadKey, result: await new DiscrepancyService().previewCorrection(discrepancy.id, payload()) });
  });

  const apply = () => run(async () => {
    await new DiscrepancyService().addCorrection(discrepancy.id, payload());
    onApplied();
  });

  const changeWho = (next: Who | null) => {
    if (!next) return;
    setWho(next);
    setPicked(null);
  };

  const ready = Boolean(picked?.matched) && (what !== "set_value" || (field && value.trim()));
  const money = (amount: string) => `$${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>New correction</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Typography fontWeight={600} fontSize={14}>1 · Who</Typography>
        <ToggleButtonGroup size="small" exclusive value={who} onChange={(_, v) => changeWho(v)}>
          <ToggleButton value="ids" sx={{ textTransform: "none" }}>Paste ids</ToggleButton>
          <ToggleButton value="missing" disabled={!canCompare} sx={{ textTransform: "none" }}>
            Missing from this file
          </ToggleButton>
          <ToggleButton value="new" disabled={!canCompare || discrepancy.holds_file} sx={{ textTransform: "none" }}>
            New in this file
          </ToggleButton>
        </ToggleButtonGroup>
        {who === "ids" ? (
          <TextField size="small" multiline minRows={4} label="Ids, one per line or separated by commas"
            value={ids} onChange={(e) => { setIds(e.target.value); setPicked(null); }}
            InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }} />
        ) : (
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            {discrepancy.file?.file_name} compared with the population's file before it.
          </Typography>
        )}
        <div>
          <Button size="small" variant="outlined" onClick={check} disabled={busy || (who === "ids" && !ids.trim())}
            sx={{ textTransform: "none" }}>
            Check who matches
          </Button>
          {picked && (
            <Typography component="span" sx={{ fontSize: 13, ml: 1.5 }}>
              {picked.matched.toLocaleString()} in this population
              {picked.not_found.length > 0 && ` · not found: ${picked.not_found.slice(0, 10).join(", ")}${picked.not_found.length > 10 ? ` and ${picked.not_found.length - 10} more` : ""}`}
            </Typography>
          )}
        </div>

        <Typography fontWeight={600} fontSize={14}>2 · What happens to them</Typography>
        <RadioGroup value={what} onChange={(e) => setWhat(e.target.value as What)}>
          {(Object.keys(WHAT) as What[]).map((key) => (
            <FormControlLabel key={key} value={key} control={<Radio size="small" />} sx={{ alignItems: "flex-start", mb: 1 }}
              label={
                <span>
                  <Typography component="span" fontWeight={600} fontSize={13}>{WHAT[key].title}</Typography>
                  <Typography fontSize={12} color="#475569">{WHAT[key].detail}</Typography>
                </span>
              } />
          ))}
        </RadioGroup>
        {what === "set_value" && (
          <>
            <TextField select size="small" label="Field" value={fieldKey}
              onChange={(e) => { setFieldKey(e.target.value); setDimension({}); }}>
              {fields.map((f) => (
                <MenuItem key={`${f.entity_type}.${f.attr_key}`} value={`${f.entity_type}.${f.attr_key}`}>
                  {f.label} ({f.entity_type})
                </MenuItem>
              ))}
            </TextField>
            {field?.dimension_keys.map((key) => (
              <TextField key={key} size="small" label={key} value={dimension[key] ?? ""}
                onChange={(e) => setDimension({ ...dimension, [key]: e.target.value })} />
            ))}
            <TextField size="small" label="Correct value" value={value} onChange={(e) => setValue(e.target.value)}
              helperText={field?.base_kind === "bool" ? "Yes or No" : undefined} />
            {picked && picked.other_populations.length > 0 && (
              <Alert severity="info">
                These people are also in {picked.other_populations.join(", ")}. The value is fixed there too.
              </Alert>
            )}
          </>
        )}

        <Typography fontWeight={600} fontSize={14}>3 · Starting from</Typography>
        <RadioGroup value={startFrom === null ? "next" : "file"}
          onChange={(e) => setStartRunId(e.target.value === "next" ? null : (discrepancy.file?.id ?? files[0]?.id ?? null))}>
          <FormControlLabel value="next" control={<Radio size="small" />} sx={{ alignItems: "flex-start", mb: 1 }}
            label={
              <span>
                <Typography component="span" fontWeight={600} fontSize={13}>The next file</Typography>
                <Typography fontSize={12} color="#475569">Everything already loaded stays as it is.</Typography>
              </span>
            } />
          <FormControlLabel value="file" disabled={!pastStartAllowed || files.length === 0} control={<Radio size="small" />}
            sx={{ alignItems: "flex-start" }}
            label={
              <span>
                <Typography component="span" fontWeight={600} fontSize={13}>The file it went wrong in</Typography>
                <Typography fontSize={12} color="#475569">
                  {pastStartAllowed
                    ? "What that file and the later ones produced is discarded and rebuilt with the correction. Everything before it stays. Refused if it reaches a month already billed."
                    : discrepancy.holds_file
                      ? "The held file hasn't loaded; it loads with this correction applied."
                      : "Stop serving starts at the next file."}
                </Typography>
              </span>
            } />
        </RadioGroup>
        {startFrom !== null && (
          <TextField select size="small" label="File" value={startFrom}
            onChange={(e) => setStartRunId(Number(e.target.value))}>
            {files.map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.file_name ?? `File ${f.id}`} · {formatDay(f.as_of)}</MenuItem>
            ))}
          </TextField>
        )}

        <Typography fontWeight={600} fontSize={14}>4 · What this does</Typography>
        {previewed ? (
          <Alert severity={previewed.refused ? "error" : previewed.rebuilds ? "warning" : "info"} icon={false}>
            {previewed.refused && <Typography fontSize={13} fontWeight={600} sx={{ mb: 1 }}>{previewed.refused}</Typography>}
            <Typography fontSize={13}>
              <strong>{previewed.matched.toLocaleString()} people</strong>
              {previewed.not_found.length > 0 && ` (${previewed.not_found.length} not found)`}
              {WHAT[what].action !== "set_value" && `, ${previewed.served_now.toLocaleString()} being served now`}
              . From {formatDay(previewed.effective_from)}
              {previewed.rebuilds
                ? `, rebuilding ${previewed.populations.reduce((n, p) => n + p.files_replayed, 0)} file(s).`
                : ", the next file on. Nothing already loaded changes."}
            </Typography>
            {previewed.rebuilds && (
              <Typography fontSize={13} sx={{ mt: 0.5 }}>
                {WHAT[what].action === "set_value"
                  ? `Discards and re-derives ${previewed.conversions_from_start.toLocaleString()} conversion(s) from the start date`
                  : `Discards ${previewed.conversions_from_start.toLocaleString()} conversion(s) from the start date`}
                , worth <strong>{money(previewed.invoice_from_start)}</strong> on the next invoice.
                {previewed.populations.length > 1 && (
                  <span> Per population: {previewed.populations.map((p) =>
                    `${p.name} ${p.conversions_from_start} (${money(p.invoice_from_start)})`).join(", ")}.</span>
                )}
                <span style={{ color: "#64748b" }}> Estimated from the conversions as they are now.</span>
              </Typography>
            )}
            {previewed.other_populations.length > 0 && (
              <Typography fontSize={13} sx={{ mt: 0.5 }}>Also fixed in {previewed.other_populations.join(", ")}.</Typography>
            )}
          </Alert>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Preview to see who this touches, what a rebuild discards, and what that is worth on the next invoice.
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>The list of people is frozen when you apply.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="outlined" onClick={showPreview} disabled={busy || !ready} sx={{ textTransform: "none" }}>
          Preview
        </Button>
        <Button variant="contained" onClick={apply} disabled={busy || !ready || !previewed || Boolean(previewed.refused)}
          sx={{ textTransform: "none" }}>
          {startFrom === null ? "Apply" : "Apply and rebuild"} for {previewed?.matched?.toLocaleString() ?? picked?.matched?.toLocaleString() ?? 0} people
        </Button>
      </DialogActions>
    </Dialog>
  );
}
